import { useEffect, useState } from "react";
import api, { formatApiError, formatMoney, todayLocalIso } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, FileSpreadsheet, FileText, Filter, Image as ImgIcon } from "lucide-react";
import { exportToExcel, exportToPdf } from "@/lib/exportUtils";

export default function FotosPagos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const today = todayLocalIso();
  const firstDay = today.slice(0, 8) + "01";
  const [f, setF] = useState({ desde: firstDay, hasta: today, vendedor: "", banco: "", conciliado: "", q: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => v && params.set(k, v));
      const { data } = await api.get(`/reports/fotos-pagos?${params}`);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const columns = [
    { key: "fecha", label: "Fecha", type: "date" },
    { key: "numero", label: "Pedido" },
    { key: "cliente", label: "Cliente" },
    { key: "vendedor", label: "Vendedor" },
    { key: "monto", label: "Monto", type: "money" },
    { key: "metodo", label: "Forma pago" },
    { key: "banco", label: "Banco/Caja" },
    { key: "referencia", label: "Referencia" },
    { key: "conciliado", label: "Conciliado" },
    { key: "diferencia", label: "Diferencia", type: "money" },
    { key: "usuario", label: "Registró" },
  ];

  return (
    <div className="space-y-6" data-testid="fotos-pagos-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Catálogo</div>
        <h1 className="font-manrope font-black text-4xl mt-1 flex items-center gap-3"><Camera className="w-8 h-8" /> Fotos de Pagos</h1>
      </header>

      <Card className="p-4 border-zinc-200">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label>Desde</Label><Input type="date" value={f.desde} onChange={(e) => setF({ ...f, desde: e.target.value })} className="w-40" /></div>
          <div><Label>Hasta</Label><Input type="date" value={f.hasta} onChange={(e) => setF({ ...f, hasta: e.target.value })} className="w-40" /></div>
          <div><Label>Buscar</Label><Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Pedido o cliente…" className="w-52" /></div>
          <div><Label>Banco</Label><Input value={f.banco} onChange={(e) => setF({ ...f, banco: e.target.value })} className="w-40" /></div>
          <Button onClick={load} className="bg-[#002FA7] hover:bg-[#00227A]"><Filter className="w-4 h-4 mr-1" />Filtrar</Button>
          <div className="flex-1"></div>
          <Button variant="outline" onClick={() => exportToExcel({ title: "Fotos de pagos", filters: f, columns, rows: items, filename: `fotos_pagos_${f.desde}_${f.hasta}` })} className="gap-2"><FileSpreadsheet className="w-4 h-4" />Excel</Button>
          <Button variant="outline" onClick={() => exportToPdf({ title: "Fotos de pagos", filters: f, columns, rows: items, filename: `fotos_pagos_${f.desde}_${f.hasta}` })} className="gap-2"><FileText className="w-4 h-4" />PDF</Button>
        </div>
      </Card>

      <Card className="border-zinc-200 overflow-hidden">
        <div className="p-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="font-semibold">{items.length} pagos</div>
          <div className="text-right">
            <div className="text-[10px] uppercase text-zinc-500 font-semibold">Total</div>
            <div className="font-black text-2xl text-blue-700 tabular-nums">{formatMoney(items.reduce((s, x) => s + Number(x.monto || 0), 0))}</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Foto</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Fecha</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Pedido</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Cliente</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold text-right">Monto</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Método</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Banco/Caja</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Ref</th>
                <th className="px-3 py-2 text-[10px] uppercase font-semibold">Conciliado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading && <tr><td colSpan={9} className="text-center text-zinc-400 py-8">Cargando…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={9} className="text-center text-zinc-400 py-10">Sin pagos.</td></tr>}
              {items.map((x, i) => (
                <tr key={`${x.order_id}-${x.pago_index}`} className="hover:bg-zinc-50">
                  <td className="px-3 py-2">{x.boleta_url ? <ImgIcon className="w-4 h-4 text-emerald-600" title="Con foto" /> : <span className="text-red-500 text-xs">Sin foto</span>}</td>
                  <td className="px-3 py-2 text-xs">{x.fecha}</td>
                  <td className="px-3 py-2"><Link to={`/pedidos/${x.order_id}`} className="font-mono text-xs font-semibold text-blue-700 hover:underline">{x.numero}</Link></td>
                  <td className="px-3 py-2 text-xs">{x.cliente}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatMoney(x.monto)}</td>
                  <td className="px-3 py-2 text-xs">{x.metodo}</td>
                  <td className="px-3 py-2 text-xs">{x.caja_id ? "Caja" : (x.banco || "—")}</td>
                  <td className="px-3 py-2 text-xs font-mono">{x.referencia || "—"}</td>
                  <td className="px-3 py-2">{x.conciliado ? <span className="text-emerald-700 text-[10px] font-bold">SÍ</span> : <span className="text-orange-600 text-[10px] font-bold">NO</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
